import { ReactNode } from 'react';
import {
  WithPopoverPlacement,
  PopoverPlacement,
  ViewportPoint,
} from '@ardoq/popovers';
import {
  HoveredFieldsPopoverProps,
  HoveredMainLabelPopoverProps,
  HoveredOtherLabelPopoverProps,
  TimelineViewCommands,
} from '../types';
import { HoveredFieldsPopover } from '../items/phases/hoveredFieldsPopover';
import { ItemLabelPopoverContentRenderer } from '../items/labels/ItemLabelPopoverContent';
import { OtherLabelPopoverContentRenderer } from '../items/labels/OtherLabels';
import {
  GRAPH_NODE_ID_ATTRIBUTE,
  PHASES_ROW_INDEX_ATTRIBUTE,
  UNSET_POPVERS_ARGS,
} from './consts';
import { POPOVER_ELEMENT_TYPE } from '../consts';
import { isPopoverElementType } from '../utils';

export type TimelinePopoverState = {
  point: ViewportPoint;
  content: ReactNode;
  placement: PopoverPlacement;
};

const toPlacement = (placement: WithPopoverPlacement): PopoverPlacement =>
  placement === WithPopoverPlacement.TOP
    ? { side: 'top', overflow: 'center' }
    : { side: 'bottom', overflow: 'center' };

type PopoverStateArgs = {
  hoveredFieldsPopoverProps?: HoveredFieldsPopoverProps;
  hoveredMainLabelPopoverProps?: HoveredMainLabelPopoverProps;
  hoveredOtherLabelPopoverProps?: HoveredOtherLabelPopoverProps;
};
export const getPopoverState = ({
  hoveredFieldsPopoverProps,
  hoveredMainLabelPopoverProps,
  hoveredOtherLabelPopoverProps,
}: PopoverStateArgs): TimelinePopoverState | null => {
  if (hoveredFieldsPopoverProps) {
    const {
      popoverMilestones,
      popoverPhases,
      popoverX,
      popoverY,
      popoverPlacement,
      precision,
      i18nLocale,
    } = hoveredFieldsPopoverProps;

    return {
      content: HoveredFieldsPopover({
        popoverPhases,
        popoverMilestones,
        precision,
        i18nLocale,
      }),
      point: { x: popoverX, y: popoverY },
      placement: toPlacement(popoverPlacement),
    };
  }

  if (hoveredMainLabelPopoverProps) {
    const { popoverX, popoverY, popoverContent } = hoveredMainLabelPopoverProps;

    return {
      content: ItemLabelPopoverContentRenderer(popoverContent),
      point: { x: popoverX, y: popoverY },
      placement: { side: 'bottom', overflow: 'center' },
    };
  }

  if (hoveredOtherLabelPopoverProps) {
    const { popoverX, popoverY, title, label } = hoveredOtherLabelPopoverProps;

    return {
      content: OtherLabelPopoverContentRenderer({ title, label }),
      point: { x: popoverX, y: popoverY },
      placement: { side: 'bottom', overflow: 'center' },
    };
  }

  return null;
};

type HandleMouseMoveArgs = {
  setMouseHover: TimelineViewCommands['setMouseHover'];
  viewInstanceId: string;
};

/**
 * All that is related to mouse hover: popovers, crosshairX display, TODO hover highlighting.
 * We send the graph node id, the hovered element type and the mouse coordinates to state,
 * where we have everything we could possibly need to resolve and compute any props.
 */
export const handleMouseMove = (
  { setMouseHover, viewInstanceId }: HandleMouseMoveArgs,
  { currentTarget, target, clientX }: React.MouseEvent
) => {
  const container = (target as HTMLElement).closest(
    `[${GRAPH_NODE_ID_ATTRIBUTE}]`
  );

  const chartAreaLeft = currentTarget.getBoundingClientRect().left;
  const crosshairX = clientX - chartAreaLeft;

  if (!container) {
    setMouseHover({
      ...UNSET_POPVERS_ARGS,
      crosshairXPosition: crosshairX,
      viewInstanceId,
    });

    return;
  }

  const popoverAttribute = container.getAttribute('data-popover-element-type');

  const popoverElementType: POPOVER_ELEMENT_TYPE | null = isPopoverElementType(
    popoverAttribute
  )
    ? popoverAttribute
    : null;

  const isLabel =
    popoverElementType === POPOVER_ELEMENT_TYPE.MAIN_LABEL ||
    popoverElementType === POPOVER_ELEMENT_TYPE.OTHER_LABEL;

  const otherLabelIndex = Number(container.getAttribute('data-label-index'));

  const hoveredGraphNodeId =
    container.getAttribute(GRAPH_NODE_ID_ATTRIBUTE) || null;

  const hoveredFieldRowIndex = Number(
    container.getAttribute(PHASES_ROW_INDEX_ATTRIBUTE)
  );

  const containerRect = container.getBoundingClientRect();

  const popoverX = isLabel
    ? (containerRect.right + containerRect.left) / 2
    : clientX;

  setMouseHover({
    crosshairXPosition: crosshairX,
    graphNodeId: hoveredGraphNodeId,
    popoverElementType,
    itemPhasesRowIndex: isNaN(hoveredFieldRowIndex)
      ? null
      : hoveredFieldRowIndex,
    otherLabelIndex: isNaN(otherLabelIndex) ? null : otherLabelIndex,
    popoverY: containerRect.bottom,
    popoverX,
    viewInstanceId,
  });
};
